import React from "react";

export const GuidelinesV1_0: React.FC = () => {
  return (
    <div className="p-4 sm:p-6 space-y-8 text-gray-700">
      {/* 1. Propósito */}
      <section>
        <h2 className="text-xl font-semibold mb-3 text-gray-900">1 – Propósito</h2>
        <p>
          A Interlaced é um ambiente privado, seletivo e tecnológico criado para promover conexões 
          interpessoais baseadas em respeito, afinidade e elegância. Estas Diretrizes orientam o 
          comportamento dos membros e definem as boas práticas necessárias para garantir uma 
          comunidade segura, autêntica e sofisticada.
        </p>
      </section>

      {/* 2. Princípios Fundamentais */}
      <section>
        <h2 className="text-xl font-semibold mb-3 text-gray-900">2 – Princípios Fundamentais</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Respeito mútuo:</strong> trate todos os membros com educação, empatia e consideração.</li>
          <li><strong>Autenticidade:</strong> seja você mesmo. Perfis falsos, duplicados ou enganosos são proibidos.</li>
          <li><strong>Discrição:</strong> proteja sua privacidade e a dos outros usuários.</li>
          <li><strong>Segurança:</strong> aja com cautela ao compartilhar informações e siga as recomendações de segurança.</li>
          <li><strong>Consentimento:</strong> toda interação deve ser voluntária, recíproca e respeitosa.</li>
          <li><strong>Integridade digital:</strong> use a tecnologia com responsabilidade — a IA da plataforma existe para aprimorar sua experiência, não para distorcê-la.</li>
        </ul>
      </section>

      {/* 3. Condutas Esperadas */}
      <section>
        <h2 className="text-xl font-semibold mb-3 text-gray-900">3 – Condutas Esperadas</h2>
        <p className="mb-3">Os usuários devem:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Cumprir os Termos de Uso, a Política de Privacidade e a legislação vigente.</li>
          <li>Utilizar a plataforma de forma ética e dentro dos limites legais.</li>
          <li>Respeitar todos os membros e adotar uma comunicação cortês.</li>
          <li>Manter suas informações e fotos autênticas, sem o uso de IA ou filtros que alterem significativamente a aparência.</li>
          <li><strong>Clareza de intenções:</strong> seja transparente quanto aos seus objetivos na plataforma. A Interlaced foi criada para promover conexões autênticas, pautadas em afinidade, reciprocidade e propósito. Deixe claro o que você busca — seja companhia, mentoria, networking ou relacionamentos significativos — sempre com respeito e honestidade. Ambiguidade ou manipulação de expectativas violam o espírito da comunidade.</li>
          <li>Aceitar o uso de filtros automatizados de verificação por inteligência artificial da Interlaced, desenvolvidos para detectar comportamentos inadequados, spam, bots e conteúdo proibido.</li>
          <li>Reportar qualquer uso indevido da IA ou tentativa de manipular os mecanismos automáticos da plataforma.</li>
        </ul>
      </section>

      {/* 4. Condutas Proibidas */}
      <section>
        <h2 className="text-xl font-semibold mb-3 text-gray-900">4 – Condutas Proibidas</h2>
        <p className="mb-3">É estritamente proibido:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Criar ou usar perfis falsos, duplicados ou automatizados (bots, scripts, robôs).</li>
          <li>Enviar conteúdo sexualmente explícito, violento, ofensivo, discriminatório ou ilegal.</li>
          <li>Solicitar, oferecer ou intermediar serviços sexuais, favores, presentes ou pagamentos em troca de interações.</li>
          <li>Divulgar produtos, serviços ou links externos, inclusive para OnlyFans, Privacy, Fansly, Patreon ou plataformas similares.</li>
          <li>Compartilhar ou solicitar informações pessoais, documentos, dados bancários ou senhas.</li>
          <li>Espalhar spam, scams, vírus, phishing, malware ou qualquer código malicioso.</li>
          <li>Manipular, interferir ou tentar burlar os algoritmos e sistemas de IA da Interlaced.</li>
          <li>Enviar ou compartilhar conteúdos protegidos por direitos autorais ou marcas registradas sem autorização — incluindo materiais sob proteção DMCA (Digital Millennium Copyright Act).</li>
          <li>Praticar pedofilia, aliciamento, tráfico de pessoas, exploração sexual ou qualquer crime contra menores.</li>
          <li>Publicar conteúdo falso, enganoso, difamatório ou que viole direitos de imagem e privacidade.</li>
        </ul>
      </section>

      {/* 5. Denúncias e Canal de Suporte */}
      <section>
        <h2 className="text-xl font-semibold mb-3 text-gray-900">5 – Denúncias e Canal de Suporte</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>Qualquer usuário pode denunciar condutas abusivas, fraudes ou conteúdos ilegais.</li>
          <li>As denúncias são confidenciais e analisadas por uma equipe especializada.</li>
          <li>A plataforma poderá aplicar medidas imediatas em casos graves, inclusive banimento e comunicação às autoridades.</li>
          <li>Denúncias falsas, abusivas ou infundadas poderão resultar em penalidade ao denunciante.</li>
          <li>Canal oficial: <a href="mailto:contato@interlaced.app" className="text-blue-600 hover:underline">contato@interlaced.app</a></li>
        </ul>
      </section>

      {/* 6. Penalidades e Medidas Disciplinares */}
      <section>
        <h2 className="text-xl font-semibold mb-3 text-gray-900">6 – Penalidades e Medidas Disciplinares</h2>
        
        {/* Tabela para desktop */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Nível</th>
                <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Penalidade</th>
                <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Situação típica</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-300 px-4 py-2">1</td>
                <td className="border border-gray-300 px-4 py-2">Advertência formal</td>
                <td className="border border-gray-300 px-4 py-2">conduta leve, linguagem inadequada, violação pontual</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2">2</td>
                <td className="border border-gray-300 px-4 py-2">Suspensão temporária (3 a 30 dias)</td>
                <td className="border border-gray-300 px-4 py-2">reincidência, assédio, uso indevido de IA ou manipulação de perfis</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2">3</td>
                <td className="border border-gray-300 px-4 py-2">Banimento definitivo</td>
                <td className="border border-gray-300 px-4 py-2">fraude, comercialização, conteúdo ilegal, pornografia infantil, tráfico, exploração</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2">4</td>
                <td className="border border-gray-300 px-4 py-2">Comunicação às autoridades</td>
                <td className="border border-gray-300 px-4 py-2">crimes previstos no Código Penal ou violação grave da LGPD / DMCA</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Cards para mobile */}
        <div className="sm:hidden space-y-4">
          <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
            <p className="font-semibold text-gray-900 mb-2">Nível 1: Advertência formal</p>
            <p className="text-sm text-gray-600">Situação: conduta leve, linguagem inadequada, violação pontual</p>
          </div>
          <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
            <p className="font-semibold text-gray-900 mb-2">Nível 2: Suspensão temporária (3 a 30 dias)</p>
            <p className="text-sm text-gray-600">Situação: reincidência, assédio, uso indevido de IA ou manipulação de perfis</p>
          </div>
          <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
            <p className="font-semibold text-gray-900 mb-2">Nível 3: Banimento definitivo</p>
            <p className="text-sm text-gray-600">Situação: fraude, comercialização, conteúdo ilegal, pornografia infantil, tráfico, exploração</p>
          </div>
          <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
            <p className="font-semibold text-gray-900 mb-2">Nível 4: Comunicação às autoridades</p>
            <p className="text-sm text-gray-600">Situação: crimes previstos no Código Penal ou violação grave da LGPD / DMCA</p>
          </div>
        </div>
      </section>

      {/* 7. Segurança e Boas Práticas */}
      <section>
        <h2 className="text-xl font-semibold mb-3 text-gray-900">7 – Segurança e Boas Práticas</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>Use senhas fortes e exclusivas para sua conta.</li>
          <li>Ative a autenticação em duas etapas (2FA) assim que estiver disponível.</li>
          <li>Não compartilhe credenciais ou dados pessoais com outros usuários.</li>
          <li>Evite acessos em redes públicas ou dispositivos compartilhados.</li>
          <li>Prefira conversas dentro da plataforma e nunca envie dinheiro a desconhecidos.</li>
          <li>Denuncie imediatamente qualquer tentativa de golpe, chantagem ou comportamento suspeito.</li>
          <li>Mantenha o aplicativo sempre atualizado para garantir a proteção das suas informações.</li>
        </ul>
      </section>

      {/* 8. Política de Reintegração */}
      <section>
        <h2 className="text-xl font-semibold mb-3 text-gray-900">8 – Política de Reintegração</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>Usuários suspensos por infrações leves podem solicitar reavaliação após 30 dias, mediante justificativa formal.</li>
          <li>Infrações graves ou criminais não possuem possibilidade de retorno.</li>
        </ul>
      </section>

      {/* 9. Transparência e Atualizações */}
      <section>
        <h2 className="text-xl font-semibold mb-3 text-gray-900">9 – Transparência e Atualizações</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>A Interlaced poderá atualizar estas Diretrizes sempre que necessário.</li>
          <li>Alterações relevantes serão comunicadas dentro da plataforma.</li>
          <li>A versão mais recente estará sempre disponível dentro da plataforma na seção de Configurações.</li>
        </ul>
      </section>

      {/* Rodapé */}
      <div className="pt-6 border-t border-gray-200 text-sm text-gray-500">
        <p>Versão 1.0 — 20 de Janeiro de 2025</p>
        <p className="mt-2">
          Ao usar a Interlaced, você concorda em seguir estas Diretrizes da Comunidade.
        </p>
      </div>
    </div>
  );
};
